import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Order } from "risa-data-model";

import { QueryConstraint } from "firebase/firestore";

import { FirestoreService } from "../../../../shared/api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../../shared/api/firebase/references";
import { logDataToConsole } from "../../../utils/customLogger";

interface OrderSDetails {
  data?: Order[] | null;
  count: number | null;
  loading: boolean;
  countLoading?: boolean;
  error: string | null;
}

const initialState: OrderSDetails = {
  data: null,
  loading: false,
  error: null,
  countLoading: false,
  count: null,
};

const errorOrdersDetailsSlice = createSlice({
  name: "errorOrdersDetails",
  initialState,
  reducers: {
    fetchErrorOrdersStart(state) {
      state.loading = true;
      state.error = null;
    },
    appendErrorOrders(state, action: PayloadAction<Order[]>) {
      state.data = state.data
        ? [...state.data, ...action.payload]
        : action.payload;
    },
    fetchErrorOrdersSuccess(state, action: PayloadAction<Order[]>) {
      state.data = action.payload;
      state.loading = false;
    },
    fetchErrorOrdersFailure(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.loading = false;
    },

    startFetchingCountsForErrorOrders(state) {
      state.countLoading = true;
      state.error = null;
      state.count = 0;
    },
    addCountsForErrorOrders(state, action: PayloadAction<number>) {
      state.count = action.payload;
      state.countLoading = false;
    },
    resetErrorOrders(state) {
      state.data = null;
      state.loading = false;
      state.error = null;
    },
    fetchSearchedErrorOrders(state, action: PayloadAction<Order[]>) {
      state.data = action.payload;
      state.loading = false;
    },
  },
});

export const {
  fetchErrorOrdersStart,
  fetchErrorOrdersSuccess,
  fetchErrorOrdersFailure,
  resetErrorOrders,
  appendErrorOrders,
  addCountsForErrorOrders,
  fetchSearchedErrorOrders,
  startFetchingCountsForErrorOrders,
} = errorOrdersDetailsSlice.actions;

export const fetchErrorOrdersFromFirebase =
  (limitingValue?: number, ...queryConstraints: QueryConstraint[]) =>
  async (dispatch) => {
    dispatch(fetchErrorOrdersStart());
    const unsubscribe =
      FirestoreService.listenToQueryCollectionForFirstBatch<Order>(
        FirestoreCollectionReference.orders(),
        limitingValue || 10,
        (orders) => {
          dispatch(fetchErrorOrdersSuccess(orders));
          logDataToConsole("Error orders fetched", orders);
        },
        (error) => {
          dispatch(fetchErrorOrdersFailure(error.message));
        },
        ...queryConstraints,
      );

    return unsubscribe;
  };

export const getSearchResultForErrorOrdersFromFirebase =
  (...queryConstraints: QueryConstraint[]) =>
  async (dispatch) => {
    dispatch(fetchErrorOrdersStart());
    const unsubscribe = FirestoreService.listenToQueryCollection<Order>(
      FirestoreCollectionReference.orders(),
      (orders) => {
        dispatch(fetchSearchedErrorOrders(orders));
        dispatch(fetchErrorOrdersCountFromFirebase(...queryConstraints));
      },
      (error) => {
        dispatch(fetchErrorOrdersFailure(error.message));
      },
      ...queryConstraints,
    );
    return unsubscribe;
  };

export const fetchNextErrorOrdersFromFirebase =
  (limitingValue?: number, ...queryConstraints: QueryConstraint[]) =>
  async (dispatch) => {
    const unsubscribe =
      FirestoreService.listenToQueryCollectionForFirstBatch<Order>(
        FirestoreCollectionReference.orders(),
        limitingValue || 10,
        (orders) => {
          dispatch(fetchErrorOrdersSuccess(orders));
        },
        (error) => {
          dispatch(fetchErrorOrdersFailure(error.message));
        },
        ...queryConstraints,
      );

    return unsubscribe;
  };

export const fetchErrorOrdersCountFromFirebase =
  (...queryConstraints: QueryConstraint[]) =>
  async (dispatch) => {
    dispatch(startFetchingCountsForErrorOrders());
    const count = await FirestoreService.getAllDocumentCount(
      FirestoreCollectionReference.orders(),
      ...queryConstraints,
    );

    dispatch(addCountsForErrorOrders(count));
    return count;
  };

export default errorOrdersDetailsSlice.reducer;
